import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Users, DollarSign, TrendingUp, Gift, Shield, UserPlus, Loader2, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

export default function AdminDashboardPage() {
  const { authHeaders, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUsers, setShowUsers] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/dashboard`, authHeaders()),
      axios.get(`${API}/admin/users`, authHeaders())
    ]).then(([statsRes, usersRes]) => {
      setStats(statsRes.data);
      setUsers(usersRes.data);
    }).catch(err => {
      if (err.response?.status === 403) toast.error('Admin access required');
      else toast.error('Failed to load dashboard');
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const handleSetAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/set-admin/${encodeURIComponent(newAdminEmail)}`, {}, authHeaders());
      toast.success(`${newAdminEmail} is now an admin`);
      setNewAdminEmail('');
      const res = await axios.get(`${API}/admin/users`, authHeaders());
      setUsers(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add admin');
    }
  };

  const handleRemoveAdmin = async (email) => {
    if (!window.confirm(`Remove admin role from ${email}?`)) return;
    try {
      await axios.post(`${API}/admin/remove-admin/${encodeURIComponent(email)}`, {}, authHeaders());
      toast.success('Admin role removed');
      const res = await axios.get(`${API}/admin/users`, authHeaders());
      setUsers(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Permanently delete ${email} and ALL their data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, authHeaders());
      toast.success(`${email} deleted`);
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, authHeaders()),
        axios.get(`${API}/admin/users`, authHeaders())
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F2F7F4]"><Navbar /><div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#2D6A4F]" size={24} /></div></div>
  );

  if (!stats) return (
    <div className="min-h-screen bg-[#F2F7F4]"><Navbar /><div className="max-w-4xl mx-auto px-4 py-12 text-center"><Shield size={48} className="mx-auto text-[#4B6B58]/40 mb-4" /><h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">Admin Access Required</h2><p className="text-[#4B6B58] mt-2">You don't have permission to view this page.</p></div></div>
  );

  const StatCard = ({ icon: Icon, label, value, sub, color = '#1B4332' }) => (
    <div className="bg-white rounded-[1.5rem] p-5 shadow-[0_4px_12px_rgb(27,67,50,0.04)]">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
          <Icon size={18} style={{ color }} />
        </div>
        <p className="text-xs font-semibold text-[#4B6B58] uppercase tracking-wider">{label}</p>
      </div>
      <p className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#1B4332]">{value}</p>
      {sub && <p className="text-xs text-[#4B6B58] mt-1">{sub}</p>}
    </div>
  );

  const statusColor = (s) => {
    if (s === 'active') return 'bg-green-100 text-green-700';
    if (s === 'trial') return 'bg-blue-100 text-blue-700';
    if (s === 'cancelled') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-fade-in mb-8">
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332]" data-testid="admin-title">Admin Dashboard</h1>
          <p className="text-[#4B6B58] mt-1">Platform performance & management</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 animate-fade-in-delay-1">
          <StatCard icon={Users} label="Total Users" value={stats.users.total} sub={`${stats.users.active} active, ${stats.users.trial} trial`} />
          <StatCard icon={DollarSign} label="Total Revenue" value={`$${stats.revenue.total.toLocaleString()}`} sub={`${Object.keys(stats.revenue.monthly).length} payment months`} color="#2D6A4F" />
          <StatCard icon={Gift} label="Referrals" value={stats.referrals.total} sub={`$${stats.referrals.credits_earned} credits earned`} color="#8B6914" />
          <StatCard icon={TrendingUp} label="Total Horses" value={stats.total_horses} sub={`${stats.users.cancelled} cancelled users`} color="#92400E" />
        </div>

        {/* User Breakdown */}
        <div className="grid sm:grid-cols-2 gap-6 mb-8 animate-fade-in-delay-2">
          {/* Subscription Breakdown */}
          <div className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(27,67,50,0.06)]">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4">Subscription Breakdown</h3>
            <div className="space-y-3">
              {[
                { label: 'Active Subscribers', value: stats.users.active, color: '#2D6A4F' },
                { label: 'Trial Users', value: stats.users.trial, color: '#3B82F6' },
                { label: 'Cancelled', value: stats.users.cancelled, color: '#F59E0B' },
                { label: 'Expired', value: stats.users.expired, color: '#9CA3AF' },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-sm text-[#4B6B58]">{item.label}</span>
                  </div>
                  <span className="font-semibold text-[#1B4332]">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Revenue by Month */}
          <div className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(27,67,50,0.06)]">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4">Monthly Revenue</h3>
            {Object.keys(stats.revenue.monthly).length === 0 ? (
              <p className="text-sm text-[#4B6B58]">No revenue data yet.</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(stats.revenue.monthly).slice(-6).map(([month, amount]) => (
                  <div key={month} className="flex items-center justify-between">
                    <span className="text-sm text-[#4B6B58]">{month}</span>
                    <span className="font-semibold text-[#2D6A4F]">${amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Signup Trends */}
        {Object.keys(stats.signup_trends).length > 0 && (
          <div className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(27,67,50,0.06)] mb-8">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4">Signup Trends</h3>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(stats.signup_trends).map(([month, count]) => (
                <div key={month} className="bg-[#E9EFE8] rounded-xl px-4 py-2 text-center">
                  <p className="text-[10px] text-[#4B6B58] font-semibold">{month}</p>
                  <p className="text-lg font-bold text-[#1B4332]">{count}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admin Management */}
        <div className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(27,67,50,0.06)] mb-8">
          <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4">Manage Admins</h3>
          <form onSubmit={handleSetAdmin} className="flex gap-3 mb-4">
            <input
              type="email"
              value={newAdminEmail}
              onChange={e => setNewAdminEmail(e.target.value)}
              placeholder="user@email.com"
              required
              data-testid="add-admin-input"
              className="stable-input flex-1"
            />
            <button type="submit" data-testid="add-admin-button" className="px-5 py-2 rounded-full bg-[#1B4332] text-white text-sm font-semibold btn-lift hover:bg-[#2D6A4F]">
              <UserPlus size={16} />
            </button>
          </form>
          <div className="space-y-2">
            {users.filter(u => u.is_admin).map(u => (
              <div key={u.id} className="flex items-center justify-between bg-[#E9EFE8] rounded-xl px-4 py-2">
                <div>
                  <span className="text-sm font-medium text-[#1B4332]">{u.name}</span>
                  <span className="text-xs text-[#4B6B58] ml-2">{u.email}</span>
                </div>
                <button onClick={() => handleRemoveAdmin(u.email)} className="text-xs text-red-400 hover:text-red-600">Remove</button>
              </div>
            ))}
          </div>
        </div>

        {/* All Users List */}
        <div className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(27,67,50,0.06)]">
          <button onClick={() => setShowUsers(!showUsers)} className="flex items-center justify-between w-full" data-testid="toggle-users-list">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332]">All Users ({users.length})</h3>
            {showUsers ? <ChevronUp size={20} className="text-[#4B6B58]" /> : <ChevronDown size={20} className="text-[#4B6B58]" />}
          </button>
          {showUsers && (
            <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
              {users.map(u => (
                <div key={u.id} className="flex items-center justify-between bg-[#F9F7F3] rounded-xl px-4 py-3" data-testid={`user-row-${u.id}`}>
                  <div>
                    <span className="text-sm font-medium text-[#1B4332]">{u.name || 'No name'}</span>
                    <span className="text-xs text-[#4B6B58] ml-2">{u.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusColor(u.subscription_status)}`}>
                      {u.subscription_status || 'inactive'}
                    </span>
                    {u.is_admin && <Shield size={12} className="text-[#2D6A4F]" />}
                    {!u.is_admin && (
                      <button onClick={() => handleDeleteUser(u.id, u.email)} data-testid={`delete-user-${u.id}`} className="p-1 rounded-full hover:bg-red-50 text-red-300 hover:text-red-500" title="Delete user">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
